(function() {
  class TpsInstallationCartSync {
    constructor() {
      const hasCartPage = document.body.classList.contains('template-cart');
      const hasCartDrawer = Boolean(document.querySelector('#CartDrawerForm'));
      if (!hasCartPage && !hasCartDrawer) return;
      this.processing = false;
      this.pendingForceRebuild = false;
      this.handleToggleChange = this.handleToggleChange.bind(this);
      this.handleCartUpdated = this.handleCartUpdated.bind(this);
      document.addEventListener('change', this.handleToggleChange);
      document.addEventListener('cart:updated', this.handleCartUpdated);
      this.syncAndRefresh();
    }

    async fetchCart() {
      const response = await fetch(`/cart.js?t=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      return response.json();
    }

    async fetchCartSections() {
      const response = await fetch('/cart?sections=tps-cart-free-items,cart-items-num', {
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch cart sections');
      }
      return response.json();
    }

    async broadcastCartContentUpdate(cart, options = {}) {
      const { open = false } = options;
      try {
        const sections = await this.fetchCartSections();
        document.dispatchEvent(new CustomEvent('cart:updateContent', {
          detail: {
            sections,
            items: Array.isArray(cart?.items) ? cart.items : [],
            open
          }
        }));
      } catch (error) {
        console.error('[tps-installation-cart-sync] Failed to broadcast cart:updateContent', error);
      }
    }

    getLineProperty(item, key) {
      return item && item.properties ? item.properties[key] : '';
    }
    parseSvcMeta(value = '') {
      const meta = {};
      const raw = `${value || ''}`.trim();
      if (!raw) return meta;
      const parts = raw.split('|');
      for (const part of parts) {
        if (!part) continue;
        const index = part.indexOf('=');
        if (index < 0) continue;
        const key = part.slice(0, index).trim();
        const val = part.slice(index + 1).trim();
        if (!key) continue;
        meta[key] = val;
      }
      return meta;
    }
    getItemSvcMeta(item) {
      return this.parseSvcMeta(this.getLineProperty(item, '_svc_meta'));
    }
    isInstallationServiceItem(item) {
      const meta = this.getItemSvcMeta(item);
      return meta.k === 's';
    }

    normalizeSeedPart(value, fallback = 'no-width') {
      const normalized = `${value || ''}`
        .toLowerCase()
        .trim()
        .replace(/["']/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      return normalized || fallback;
    }

    buildMainSeed(lineItem) {
      const meta = this.getItemSvcMeta(lineItem);
      const fromProperty = `${meta.seed || ''}`.trim();
      if (fromProperty) return fromProperty;
      const parentProductId = this.getLineProperty(lineItem, '_pid') || `${lineItem?.product_id || 'product'}`;
      const widthValue = this.getLineProperty(lineItem, '_inches_size_width')
        || this.getLineProperty(lineItem, 'Width')
        || 'no-width';
      const heightValue = this.getLineProperty(lineItem, '_inches_size_length')
        || this.getLineProperty(lineItem, 'Length')
        || 'no-height';
      const normalizedWidth = this.normalizeSeedPart(widthValue, 'no-width');
      const normalizedHeight = this.normalizeSeedPart(heightValue, 'no-height');
      return `${parentProductId}-${normalizedWidth}-${normalizedHeight}`;
    }

    getServiceUnits(quantityNum, serviceType = '') {
      const normalizedQuantity = Math.max(parseInt(quantityNum, 10) || 0, 0);
      if (normalizedQuantity < 1) return 0;
      if (`${serviceType || ''}`.trim().toLowerCase() === 'drapery') {
        return Math.max(Math.ceil(normalizedQuantity / 2), 1);
      }
      return normalizedQuantity;
    }

    formatMoney(cents) {
      if (window.theme?.Currency?.formatMoney) {
        return window.theme.Currency.formatMoney(cents, window.theme?.settings?.moneyFormat);
      }
      return `$${(cents / 100).toFixed(2)}`;
    }

    refreshSubtotal(cart) {
      if (!cart) return;
      const subtotalEl = document.querySelector('[data-subtotal]');
      if (!subtotalEl) return;
      const total = Math.max(parseInt(cart.total_price, 10) || 0, 0);
      const target = subtotalEl.querySelector('.bcpo-cart-original-total .bcpo-cart-original-total')
        || subtotalEl.querySelector('.bcpo-cart-original-total')
        || subtotalEl;
      target.innerHTML = this.formatMoney(total);
      subtotalEl.dataset.amountCents = `${total}`;
    }

    refreshSummary(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      this.refreshSubtotal(cart);
      const serviceTotal = cart.items.reduce((sum, item) => {
        return this.isInstallationServiceItem(item)
          ? sum + (item.final_line_price || 0)
          : sum;
      }, 0);
      const productsTotal = Math.max((cart.total_price || 0) - serviceTotal, 0);
      const productsAmountEl = document.querySelector('[data-products-summary-amount]');
      if (productsAmountEl) {
        productsAmountEl.innerHTML = this.formatMoney(productsTotal);
        productsAmountEl.dataset.amountCents = `${productsTotal}`;
      }
      const serviceRow = document.querySelector('[data-installation-summary-row]');
      const serviceAmountEl = document.querySelector('[data-installation-summary-amount]');
      if (serviceRow) {
        serviceRow.style.display = serviceTotal > 0 ? '' : 'none';
      }
      if (serviceAmountEl) {
        serviceAmountEl.innerHTML = this.formatMoney(serviceTotal);
        serviceAmountEl.dataset.amountCents = `${serviceTotal}`;
      }
    }

    buildMainQtyBySeed(cart) {
      const map = new Map();
      if (!cart || !Array.isArray(cart.items)) return map;
      for (const item of cart.items) {
        if (this.isInstallationServiceItem(item)) continue;
        const seed = this.buildMainSeed(item);
        if (!seed) continue;
        const qty = Math.max(parseInt(item.quantity, 10) || 0, 0);
        map.set(seed, (map.get(seed) || 0) + qty);
      }
      return map;
    }

    buildServiceAggBySeed(cart) {
      const map = new Map();
      if (!cart || !Array.isArray(cart.items)) return map;
      for (const item of cart.items) {
        if (!this.isInstallationServiceItem(item)) continue;
        const seed = this.getItemSvcMeta(item).seed;
        if (!seed) continue;
        if (!map.has(seed)) {
          map.set(seed, {
            key: item.key || '',
            quantity: 0,
            finalLinePrice: 0
          });
        }
        const agg = map.get(seed);
        agg.quantity += Math.max(parseInt(item.quantity, 10) || 0, 0);
        agg.finalLinePrice += Math.max(parseInt(item.final_line_price, 10) || 0, 0);
      }
      return map;
    }

    refreshAddonPrices(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      const mainQtyBySeed = this.buildMainQtyBySeed(cart);
      const serviceAggBySeed = this.buildServiceAggBySeed(cart);
      const addonNodes = document.querySelectorAll('[data-installation-addon]');
      addonNodes.forEach((addonNode) => {
        const parentSeed = addonNode.dataset.parentKeySeed || '';
        if (!parentSeed) return;
        const mainQty = Math.max(parseInt(mainQtyBySeed.get(parentSeed), 10) || 0, 0);
        addonNode.dataset.mainQty = `${mainQty}`;
        const checkbox = addonNode.querySelector('[data-installation-toggle]');
        const priceNode = addonNode.querySelector('[data-installation-addon-price]');
        const serviceAgg = serviceAggBySeed.get(parentSeed);
        if (serviceAgg && serviceAgg.quantity > 0) {
          addonNode.classList.add('is-checked');
          addonNode.dataset.serviceLineKey = serviceAgg.key || '';
          if (checkbox) {
            checkbox.checked = true;
            checkbox.disabled = false;
          }
          if (priceNode) {
            priceNode.innerHTML = this.formatMoney(serviceAgg.finalLinePrice);
          }
          return;
        }
        addonNode.classList.remove('is-checked');
        addonNode.dataset.serviceLineKey = '';
        const serviceVariantId = parseInt(addonNode.dataset.serviceVariantId, 10);
        const hasServiceVariant = Number.isFinite(serviceVariantId) && serviceVariantId > 0;
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = !hasServiceVariant;
        }
        if (priceNode) {
          const unitPrice = parseFloat(addonNode.dataset.serviceUnitPrice || '0');
          const serviceType = `${addonNode.dataset.serviceType || ''}`.trim().toLowerCase();
          const units = this.getServiceUnits(mainQty, serviceType);
          const estimatedCents = Number.isFinite(unitPrice) && unitPrice > 0
            ? Math.round(unitPrice * units * 100)
            : 0;
          priceNode.innerHTML = this.formatMoney(estimatedCents);
        }
      });
    }

    async refreshSummaryFromServer() {
      try {
        const cart = await this.fetchCart();
        this.refreshSummary(cart);
        this.refreshAddonPrices(cart);
      } catch (error) {
        console.error('[tps-installation-cart-sync] Failed to refresh summary', error);
      }
    }

    async syncServiceQuantities(cart) {
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) return false;
      const mainQtyBySeed = new Map();
      const mainServiceTypeBySeed = new Map();
      for (const item of cart.items) {
        if (this.isInstallationServiceItem(item)) continue;
        const seed = this.buildMainSeed(item);
        if (!seed) continue;
        const qty = Math.max(parseInt(item.quantity, 10) || 0, 0);
        mainQtyBySeed.set(seed, (mainQtyBySeed.get(seed) || 0) + qty);
        const serviceTypeFromMain = `${this.getItemSvcMeta(item).t || ''}`.trim().toLowerCase();
        if (serviceTypeFromMain && !mainServiceTypeBySeed.has(seed)) {
          mainServiceTypeBySeed.set(seed, serviceTypeFromMain);
        }
      }
      const servicesBySeed = new Map();
      const orphanServiceLines = [];
      for (const item of cart.items) {
        if (!this.isInstallationServiceItem(item)) continue;
        const parentSeed = this.getItemSvcMeta(item).seed;
        if (!parentSeed) {
          orphanServiceLines.push(item);
          continue;
        }
        if (!servicesBySeed.has(parentSeed)) {
          servicesBySeed.set(parentSeed, []);
        }
        servicesBySeed.get(parentSeed).push(item);
      }
      const updates = [];
      for (const orphanLine of orphanServiceLines) {
        updates.push({ key: orphanLine.key, quantity: 0 });
      }
      for (const [parentSeed, serviceLines] of servicesBySeed.entries()) {
        const mainQty = Math.max(parseInt(mainQtyBySeed.get(parentSeed), 10) || 0, 0);
        if (mainQty < 1) {
          for (const serviceLine of serviceLines) {
            updates.push({ key: serviceLine.key, quantity: 0 });
          }
          continue;
        }
        const [primaryServiceLine, ...extraServiceLines] = serviceLines;
        const serviceTypeFromService = `${this.getItemSvcMeta(primaryServiceLine).t || ''}`.trim().toLowerCase();
        const serviceType = serviceTypeFromService || mainServiceTypeBySeed.get(parentSeed) || '';
        const targetQty = this.getServiceUnits(mainQty, serviceType);
        if ((primaryServiceLine.quantity || 0) !== targetQty) {
          updates.push({ key: primaryServiceLine.key, quantity: targetQty });
        }
        for (const extraServiceLine of extraServiceLines) {
          updates.push({ key: extraServiceLine.key, quantity: 0 });
        }
      }
      for (const update of updates) {
        await window.theme.cart.changeItem(update.key, update.quantity);
      }
      return updates.length > 0;
    }

    async syncAndRefresh(options = {}) {
      const { forceRebuild = false } = options;
      if (this.processing) {
        this.pendingForceRebuild = this.pendingForceRebuild || forceRebuild;
        return;
      }
      this.processing = true;
      try {
        const cart = await this.fetchCart();
        const hasQuantityChanged = await this.syncServiceQuantities(cart);
        const needRebuild = forceRebuild || hasQuantityChanged;
        const finalCart = hasQuantityChanged ? await this.fetchCart() : cart;
        if (needRebuild) {
          document.dispatchEvent(new CustomEvent('cart:build'));
          setTimeout(() => this.refreshSummaryFromServer(), 500);
        }
        this.refreshSummary(finalCart);
        this.refreshAddonPrices(finalCart);
        this.broadcastCartContentUpdate(finalCart, { open: false });
      } catch (error) {
        console.error('[tps-installation-cart-sync] Sync failed', error);
      } finally {
        this.processing = false;
        if (this.pendingForceRebuild) {
          const shouldForceRebuild = this.pendingForceRebuild;
          this.pendingForceRebuild = false;
          this.syncAndRefresh({ forceRebuild: shouldForceRebuild });
        }
      }
    }

    createServiceProperties(addonNode) {
      const parentKeySeed = addonNode.dataset.parentKeySeed || '';
      const serviceType = addonNode.dataset.serviceType || '';
      const zip = addonNode.dataset.serviceZip || '';
      const mainProductTitleFromRow = addonNode
        .closest('.cart__item')
        ?.querySelector('[data-main-service-for]')
        ?.textContent
        ?.trim();
      const mainProductTitle = mainProductTitleFromRow || addonNode.dataset.mainProductTitle || 'Product';
      return {
        _svc_zip: zip,
        'Service For': mainProductTitle,
        _svc_meta: `v=1|k=s|seed=${parentKeySeed}|t=${serviceType}`
      };
    }

    async addServiceLine(addonNode) {
      const variantId = parseInt(addonNode.dataset.serviceVariantId, 10);
      if (!Number.isFinite(variantId) || variantId <= 0) {
        throw new Error('Missing service variant id');
      }
      const mainQty = Math.max(parseInt(addonNode.dataset.mainQty, 10) || 1, 1);
      const serviceType = `${addonNode.dataset.serviceType || ''}`.trim().toLowerCase();
      const serviceQty = this.getServiceUnits(mainQty, serviceType);
      const parentSeed = addonNode.dataset.parentKeySeed || '';
      if (parentSeed) {
        const cart = await this.fetchCart();
        const matchedLines = cart.items.filter((item) => {
          return this.isInstallationServiceItem(item)
            && this.getItemSvcMeta(item).seed === parentSeed;
        });
        if (matchedLines.length) {
          const [primaryLine, ...extraLines] = matchedLines;
          if ((primaryLine.variant_id || primaryLine.id) === variantId) {
            if ((primaryLine.quantity || 0) !== serviceQty) {
              await window.theme.cart.changeItem(primaryLine.key, serviceQty);
            }
            for (const extraLine of extraLines) {
              await window.theme.cart.changeItem(extraLine.key, 0);
            }
            return;
          }
          for (const line of matchedLines) {
            await window.theme.cart.changeItem(line.key, 0);
          }
        }
      }
      const payload = {
        items: [{
          id: variantId,
          quantity: serviceQty,
          properties: this.createServiceProperties(addonNode)
        }]
      };
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error('Failed to add installation service');
      }
    }

    async removeServiceLine(addonNode) {
      const lineKey = addonNode.dataset.serviceLineKey || '';
      const parentSeed = addonNode.dataset.parentKeySeed || '';
      if (parentSeed) {
        const cart = await this.fetchCart();
        const matchedLines = cart.items.filter((item) => {
          return this.isInstallationServiceItem(item)
            && this.getItemSvcMeta(item).seed === parentSeed;
        });
        for (const line of matchedLines) {
          await window.theme.cart.changeItem(line.key, 0);
        }
        if (matchedLines.length) return;
      }
      if (!lineKey) return;
      await window.theme.cart.changeItem(lineKey, 0);
    }

    async handleToggleChange(event) {
      const checkbox = event.target.closest('[data-installation-toggle]');
      if (!checkbox) return;
      const addonNode = checkbox.closest('[data-installation-addon]');
      if (!addonNode) return;
      checkbox.disabled = true;
      try {
        if (checkbox.checked) {
          await this.addServiceLine(addonNode);
        } else {
          await this.removeServiceLine(addonNode);
        }
        await this.syncAndRefresh({ forceRebuild: true });
      } catch (error) {
        checkbox.checked = !checkbox.checked;
        console.error('[tps-installation-cart-sync] Toggle failed', error);
      } finally {
        checkbox.disabled = false;
      }
    }

    handleCartUpdated() {
      this.syncAndRefresh({ forceRebuild: true });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    new TpsInstallationCartSync();
  });
})();
